<template>
	<validation-form
		ref="validationForm"
		#default="{ handleSubmit }"
		tag="div"
		class="d-flex flex-column overflow-y-hidden"
		style="width: 100%;"
	>
		<ViewLayout>
			<template #header-title>
				{{ pageTitle }}
			</template>
			<template #content>
				<StepperAccordion
					ref="stepperAccordion"
					:steps="transformedSteps"
					:initial-step="currentStep + 1"
					:current-step-index="currentStep"
					:show-actions="false"
					@step-change="handleStepChange"
				>
					<template #step-0>
						<slot name="step-0" />
					</template>
					<template #step-1>
						<slot name="step-1" />
					</template>
					<template #step-2>
						<slot name="step-2" />
					</template>
					<template #step-3>
						<slot name="step-3" />
					</template>
				</StepperAccordion>
				<slot name="modal" />
			</template>
			<template #footer>
				<!-- Custom Footer with Responsive Layout -->
				<PageActionRow>
					<template #actions>
						<v-row
							dense
							class="d-flex flex-grow-1 justify-center"
						>
							<v-col
								cols="12"
								md="12"
								lg="12"
								xl="8"
								class="flex-grow-1"
							>
								<div class="d-flex justify-space-between align-center flex-grow-1">
									<SecondaryActionButton @click="backButtonAction">
										Back to Overview
									</SecondaryActionButton>
									<span class="stepper-footer__step-indicator">
										Step {{ currentStep + 1 }} of {{ steps.length }}
									</span>
									<!-- <v-spacer/> -->
									<div class="d-flex cassie-horizontal-sm">
										<SecondaryActionButton
											v-if="currentStep"
											@click="(effectiveUserFullPermissions && userHasBrand(brandId) && isEdit && integrationServiceHasChanged) ? handleSubmit(previous) : previous()"
										>
											Previous
										</SecondaryActionButton>
										<PrimaryActionButton
											v-if="currentStep === steps.length - 1 && effectiveUserFullPermissions && userHasBrand(brandId)"
											:disabled="!integrationServiceHasChanged"
											@click="handleSubmit(goToLastStep)"
										>
											{{ !isEdit && !cloneMode ? 'Finish' : 'Save Changes' }}
										</PrimaryActionButton>
										<template v-else-if="currentStep !== steps.length - 1">
											<!-- Direct to S3: Show Finish on Step 2 (index 2) -->
											<PrimaryActionButton
												v-if="isDirectToS3 && currentStep === 2 && effectiveUserFullPermissions && userHasBrand(brandId)"
												:disabled="!integrationServiceHasChanged"
												@click="handleSubmit(goToLastStep)"
											>
												{{ !isEdit && !cloneMode ? 'Finish' : 'Save Changes' }}
											</PrimaryActionButton>
											<template v-else>
												<PrimaryActionButton
													v-if="(effectiveUserFullPermissions && userHasBrand(brandId)) && isEdit"
													:disabled="!integrationServiceHasChanged"
													@click="handleSubmit(goToLastStep)"
												>
													Save Changes
												</PrimaryActionButton>
												<PrimaryActionButton
													@click="handleSubmit(next)"
												>
													Next
												</PrimaryActionButton>
											</template>
										</template>
										<PrimaryActionButton
											v-else-if="currentStep === steps.length - 2 && !userFullPermissions"
											@click="handleSubmit(next)"
										>
											Next
										</PrimaryActionButton>
									</div>
								</div>
							</v-col>
						</v-row>
					</template>
				</PageActionRow>
			</template>
		</ViewLayout>
	</validation-form>
</template>
<script>
import { mapGetters } from 'vuex'
import ViewLayout from '../../../../../../shared/layouts/view-layout.vue'
import { StepperAccordion } from '../../../../components/component-library-wrapper.js'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import ValidationForm from '../../../../../../shared/components/validation-form.vue'
import PageActionRow from '../../../../../../shared/components/page-action-row.vue'
import { userHasBrand } from '../../../../../../shared/state/brands.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { validateIntegrationServiceSchema, validateIntegrationFilters } from '../service-integration-validator.js'

export default {
	components: { ValidationForm, PrimaryActionButton, SecondaryActionButton, StepperAccordion, ViewLayout, PageActionRow },
	props: {
		backButtonAction: Function,
		brandId: Number,
		userFullPermissions: {
			type: Boolean,
			default: null
		},
		finishButtonAction: {
			type: Function,
			default: () => {}
		},
		isEdit: Boolean,
		currentStep: Number,
		steps: Array,
		integrationService: Object,
		integrationServiceHasChanged: Boolean,
		cloneMode: Boolean,
		hasValidJson: {
			type: Boolean,
			default: true
		},
		isDirectToS3: {
			type: Boolean,
			default: false
		},
		requestBodyMappingType: {
			type: String,
			default: 'classic'
		}
	},
	setup () {
		return {
			userHasBrand
		}
	},
	data () {
		return {}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		pageTitle () {
			const mode = () => {
				if (this.isEdit) {
					return 'Edit'
				} else if (this.cloneMode) {
					return 'Clone'
				}
				return 'Create'
			}
			return `${mode()} Integration`
		},
		pageCaption () {
			const mode = () => {
				if (this.isEdit) {
					return 'Edit an existing'
				} else if (this.cloneMode) {
					return 'Clone an existing'
				}
				return 'Create a new'
			}
			return `${mode()} integration`
		},
		effectiveUserFullPermissions () {
			if (this.userFullPermissions !== null) return this.userFullPermissions
			return this.productAreaPermission && this.productAreaPermission(CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS)
		},
		transformedSteps () {
			const stepSubtitles = [
				'Configure the data that you want to send to your integration system',
				'Set up the connection to your remote system',
				'Map your selected data to the payload of your integration request',
				'Configure how we should handle responses from your integration system'
			]

			return this.steps.map((step, index) => {
				const isDs3Disabled = this.isDirectToS3 && (index === 1 || index === 3)
				const disabled = isDs3Disabled || (!this.isEdit && !step.stepComplete && index > this.currentStep)
				return {
					title: step.title,
					subtitle: stepSubtitles[index] || '',
					disabled,
					disabledReason: isDs3Disabled ? 'Disabled for Authentication Type Direct to S3' : undefined,
					stepComplete: step.stepComplete
				}
			})
		}
	},
	methods: {
		/**
		 * Returns true (and shows a snackbar) when any filter has an operator but incomplete values.
		 * VeeValidate providers inside v-data-table scoped slots don't register with the
		 * outer ValidationObserver in Vue 2, so we validate the data model directly.
		 */
		hasFilterErrors () {
			const errors = validateIntegrationFilters(this.integrationService?.filters)
			if (errors.length > 0) {
				showSnackbar({ text: 'Filter validation failed - ' + errors.join(', '), color: 'error' })
				return true
			}
			return false
		},
		async goToLastStep () {
			if (this.hasFilterErrors()) return

			// Validate the current integration service excluding the name (provided later in Save modal)
			try {
				const toValidate = JSON.parse(JSON.stringify(this.integrationService))
				validateIntegrationServiceSchema(toValidate, { validateName: false, isDirectToS3: this.isDirectToS3 })
			} catch (error) {
				showSnackbar({ text: error.message || 'Invalid service configuration', color: 'error' })
				return
			}

			await this.$emit('finish')
			const steps = JSON.parse(JSON.stringify(this.steps))
			steps[this.currentStep].stepComplete = true
			this.updateSteps(steps)
			// If Direct to S3, do not navigate to step 4; stay on step 3 (index 2)
			if (this.isDirectToS3) {
				this.updateCurrentStep(2)
			} else {
				this.updateCurrentStep(this.steps.length - 1)
			}
		},
		updateCurrentStep (currentStep) {
			this.$emit('update:currentStep', currentStep)
		},
		updateSteps (steps) {
			this.$emit('update:steps', steps)
		},
		previous () {
			this.updateCurrentStep(this.currentStep - 1)
		},
		next () {
			// Block navigation if Data Selection filters are incomplete
			if (this.currentStep === 0 && this.hasFilterErrors()) return
			// Note: JSON validation warning is shown in the component itself
			// Users can proceed even with placeholders that may appear as invalid JSON
			// Skip disabled steps for Direct to S3
			if (this.isDirectToS3) {
				if (this.currentStep === 0) {
					const steps = JSON.parse(JSON.stringify(this.steps))
					steps.forEach((step, index) => {
						if (index <= this.currentStep) step.stepComplete = true
					})
					this.updateSteps(steps)
					this.updateCurrentStep(2)
					return
				}
				if (this.currentStep === 2) {
					// On Direct to S3, there is no response step; finish flow
					this.goToLastStep()
					return
				}
			}
			const steps = JSON.parse(JSON.stringify(this.steps))
			steps.forEach((step, index) => {
				if (index <= this.currentStep) step.stepComplete = true
			})
			this.updateSteps(steps)
			this.updateCurrentStep(this.currentStep + 1)
		},
		async handleStepChange (stepIndex) {
			const targetStep = stepIndex - 1

			// Revert triggers step-change again via the watcher — bail if we're already here
			if (targetStep === this.currentStep) return

			// Prevent navigating to disabled steps when Direct to S3
			if (this.isDirectToS3 && (targetStep === 1 || targetStep === 3)) {
				return
			}

			const revertStepper = () => {
				if (this.$refs.stepperAccordion) {
					this.$refs.stepperAccordion.currentStep = this.currentStep + 1
				}
			}

			// In edit mode, validate before allowing card switch — same as Next/Save buttons
			const observer = this.$refs.validationForm?.$refs?.validationObserver
			if (this.isEdit && observer) {
				const isValid = await observer.validate()
				if (!isValid) {
					revertStepper()
					const errors = observer.errors
					if (errors) {
						const errorFields = Object.keys(errors)
							.filter(field => !field.includes('_vee'))
							.map(key => ({ field: key.replace(' *', ''), errors: errors[key] }))
							.filter(field => field.errors.length > 0)
						if (errorFields.length > 0) {
							showSnackbar({
								text: 'Form invalid - ' + errorFields.map(field => `${field.field} - ${field.errors.join(',')}`).join(', '),
								color: 'error'
							})
						}
					}
					return
				}
			}

			// Block card switch if Data Selection filters are incomplete
			if (this.currentStep === 0 && this.hasFilterErrors()) {
				revertStepper()
				return
			}

			// Navigate
			if (this.isEdit) {
				this.$emit('update:currentStep', targetStep)
			} else if (targetStep <= this.currentStep || this.steps[targetStep]?.stepComplete) {
				this.$emit('update:currentStep', targetStep)
			} else {
				revertStepper()
			}
		}
	}
}
</script>

<style lang="scss" scoped>
/* Override ViewLayout padding for better space utilization */
:deep(.d-flex.flex-column.overflow-y-auto.pa-4.cassie-vertical-md) {
	padding: 16px 24px !important;
}

/* Apply SectionCard styling to stepper steps */
:deep(.stepper-accordion-v) {
	/* Main stepper container */
	.stepper-accordion-v__stepper.v-stepper {
		background: transparent !important;
		box-shadow: none !important;
	}

	.stepper-accordion-v__stepper .v-stepper {
		background: transparent !important;
		box-shadow: none !important;
	}

	/* Style step headers with SectionCard background - max specificity */
	.stepper-accordion-v__stepper .v-stepper .v-stepper-step {
		background: var(--global-main-background-color) !important;
		background-color: var(--global-main-background-color) !important;
		border: 1px solid var(--global-border-color) !important;
		border-bottom: none !important;
		border-radius: var(--radius-sm, 4px) var(--radius-sm, 4px) 0 0 !important;
		margin-bottom: 0 !important;
		padding: 16px !important;
	}

	.stepper-accordion-v__stepper .v-stepper .v-stepper-step:last-of-type {
		border-bottom: 1px solid var(--global-border-color) !important;
		border-radius: var(--radius-sm, 4px) !important;
	}

	/* Remove hover state from step headers - max specificity */
	.stepper-accordion-v__stepper .v-stepper .v-stepper-step {
		pointer-events: auto !important;
	}

	.stepper-accordion-v__stepper .v-stepper .v-stepper-step .v-stepper__step__step {
		transition: none !important;
		background-color: transparent !important;
	}

	.stepper-accordion-v__stepper .v-stepper .v-stepper-step:hover .v-stepper__step__step,
	.stepper-accordion-v__stepper .v-stepper .v-stepper-step:focus .v-stepper__step__step,
	.stepper-accordion-v__stepper .v-stepper .v-stepper-step:active .v-stepper__step__step {
		background-color: transparent !important;
		transform: none !important;
		box-shadow: none !important;
	}

	.stepper-accordion-v__stepper .v-stepper .v-stepper-step:hover,
	.stepper-accordion-v__stepper .v-stepper .v-stepper-step:focus,
	.stepper-accordion-v__stepper .v-stepper .v-stepper-step:active {
		background-color: var(--global-main-background-color) !important;
		background: var(--global-main-background-color) !important;
	}

	/* Style step content with SectionCard background - max specificity */
	.stepper-accordion-v__stepper .v-stepper .v-stepper-content {
		background: var(--global-main-background-color) !important;
		background-color: var(--global-main-background-color) !important;
		border: 1px solid var(--global-border-color) !important;
		border-top: none !important;
		border-radius: 0 0 var(--radius-sm, 4px) var(--radius-sm, 4px) !important;
		padding: 16px !important;
		margin-bottom: 16px !important;
		margin-top: 0 !important;
	}

	.stepper-accordion-v__stepper .stepper-accordion-v__content {
		background: var(--global-main-background-color) !important;
		background-color: var(--global-main-background-color) !important;
		border: 1px solid var(--global-border-color) !important;
		border-top: none !important;
		border-radius: 0 0 var(--radius-sm, 4px) var(--radius-sm, 4px) !important;
		padding: 16px !important;
		margin-bottom: 16px !important;
	}

	.stepper-accordion-v__stepper .stepper-accordion-v__step-content {
		background: var(--global-main-background-color) !important;
		background-color: var(--global-main-background-color) !important;
		padding: 0 !important;
	}

	/* Form field styling to match SectionCard form fields */
	.v-stepper-content .v-input,
	.stepper-accordion-v__content .v-input {
		margin-bottom: 16px;
	}

	.v-stepper-content .v-text-field,
	.v-stepper-content .v-select,
	.stepper-accordion-v__content .v-text-field,
	.stepper-accordion-v__content .v-select {
		background: var(--global-card-background, white);
		border-radius: var(--radius-sm, 4px);
	}

	.v-stepper-content .v-text-field__details,
	.v-stepper-content .v-select__details,
	.stepper-accordion-v__content .v-text-field__details,
	.stepper-accordion-v__content .v-select__details {
		margin-bottom: 8px;
	}

	/* Ensure proper spacing for form elements */
	.v-stepper-content .cassie-horizontal-sm > *,
	.stepper-accordion-v__content .cassie-horizontal-sm > * {
		margin-right: 16px;
	}

	.v-stepper-content .cassie-horizontal-sm > *:last-child,
	.stepper-accordion-v__content .cassie-horizontal-sm > *:last-child {
		margin-right: 0;
	}
}
/* Add styles to visually grey out disabled steps for Direct to S3 */
:deep(.direct-s3-mode .stepper-accordion-v__stepper .v-stepper .v-stepper-step:nth-of-type(2)),
:deep(.direct-s3-mode .stepper-accordion-v__stepper .v-stepper .v-stepper-step:nth-of-type(4)) {
	cursor: not-allowed !important;
	opacity: 0.75;
}

:deep(.direct-s3-mode .stepper-accordion-v__stepper .v-stepper .v-stepper-step:nth-of-type(2) .v-stepper__step__step),
:deep(.direct-s3-mode .stepper-accordion-v__stepper .v-stepper .v-stepper-step:nth-of-type(4) .v-stepper__step__step) {
	background-color: var(--global-border-color) !important;
	color: var(--global-muted-foreground) !important;
}

:deep(.direct-s3-mode .stepper-accordion-v__stepper .v-stepper .v-stepper-step:nth-of-type(2) .v-stepper__label),
:deep(.direct-s3-mode .stepper-accordion-v__stepper .v-stepper .v-stepper-step:nth-of-type(4) .v-stepper__label) {
	color: var(--global-muted-foreground) !important;
}

.disabled-steps-note {
	font-size: 12px;
	color: var(--global-muted-foreground, #616161);
}
/* Removed local disabled styling; visuals handled by StepperAccordion */
</style>
